import { useState } from 'react';
import { NotificationAlert } from '@/data/notifications';
import { HEALTH_STATES } from '@/const';
import { useFetchResource } from '@/hooks';
import { colors, HealthSeverity } from '@/constants/colors';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

interface AttachmentItemProps {
  resourceId: string;
  className?: string;
}

const AttachmentItem = ({ resourceId, className }: AttachmentItemProps) => {
  const { resource, isLoadingResource, isError, retry } = useFetchResource(resourceId);
  const [imageLoaded, setImageLoaded] = useState(false);

  const showSkeleton = isLoadingResource || (resource && !imageLoaded);

  if (isError) {
    return (
      <div className={cn('flex flex-col items-center justify-center gap-2 text-[#55687D] min-h-[200px]', className)}>
        <span>Failed to load attachment</span>
        <button onClick={retry} className="text-primary-600 hover:underline text-sm">
          Try Again
        </button>
      </div>
    );
  }

  if (!isLoadingResource && !resource) {
    return (
      <div className={cn('flex items-center justify-center min-h-[200px] text-[#55687D]', className)}>No data</div>
    );
  }

  return (
    <div className="relative">
      {showSkeleton && <Skeleton className={cn('min-h-[200px] w-full', className)} />}
      {resource && (
        <img
          src={resource}
          className={cn('w-full', showSkeleton && 'absolute invisible', className)}
          alt="Notification attachment"
          onLoad={() => setImageLoaded(true)}
        />
      )}
    </div>
  );
};

const NotificationModal = ({
  isOpen,
  onClose,
  notification,
}: {
  isOpen: boolean;
  onOpen?: () => void;
  onClose: () => void;
  notification: NotificationAlert;
}) => {
  const state = HEALTH_STATES[notification.severity];
  const IconComponent = state?.icon;
  const [activeResource, setActiveResource] = useState(0);
  const attachments = notification.attachments;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="pb-4 rounded-2xl max-w-6xl">
        <DialogHeader className="font-normal text-base">
          <div className="flex items-center gap-2">
            <div className="flex justify-center items-center w-10 h-10 rounded-full bg-gray-100">
              <IconComponent className={`text-[${colors.health[notification.severity as HealthSeverity]}] w-6 h-6`} />
            </div>
            <div className="font-semibold">
              {notification.asset_ui_name}
              {notification.component_name ? ` / ${notification.component_name}` : null} - {notification.event_ui_name}
            </div>
          </div>
        </DialogHeader>
        <div className="px-6 py-2">
          <div className="pb-4">{notification?.content}</div>

          {!!attachments.length && <AttachmentItem resourceId={attachments[activeResource]?.content} />}

          {attachments.length > 1 && (
            <div className="flex items-center gap-2">
              {attachments.map((attachment, index) => (
                <div
                  key={attachment.content}
                  className={cn(
                    'max-w-[100px] my-2 p-1 cursor-pointer',
                    index === activeResource ? 'border-2 border-black' : ''
                  )}
                  onClick={() => setActiveResource(index)}
                >
                  <AttachmentItem resourceId={attachment.content} className="min-h-0" />
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationModal;
